import {Component, Inject, Input, OnInit} from '@angular/core';
import {TdoTableActionsEnum, TdoTableModel} from '../../tdo-controls/common/tdo-table/models/tdo-table.model';
import {MatTableDataSource} from '@angular/material/table';
import {TdoTableBaseItemModel} from '../../tdo-controls/common/tdo-table/models/tdo-table-base-item.model';
import {ITdoTableItemModel} from '../../tdo-controls/common/tdo-table/interfaces/i-tdo-table-item.model';
import {StaticDataService} from '../../services/static-data.service';
import {EnumValidValueItemModel} from '../../models/enum/enum-valid-value-item.model';
import {ProceduresService} from '../../modules/configuration/procedures/procedures.service';
import {DialogService} from '../../services/dialog.service';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TdoTableEvent} from '../../tdo-controls/common/tdo-table/models/tdo-table.event';
import {ProcedureSelectionDialogModel} from './models/procedure-selection-dialog.model';

interface Form {
  id: string;
  dFormID: string;
  name: string;
  filledBy: string;
  isOpenByDefault: boolean;
  isAddByDefault: boolean;
  formOrder: number;
}

interface Procedure {
  id: string;
  code: string;
  description: string;
  name: string;
  forms: Form[];
  deletedForms: Form[];
  speciality: string; // enumMedicalSpeciality
  partOfAppointmentGroup: boolean;
  appointmentAddByDefault: boolean;
  partOfVisitGroup: boolean;
  visitAddByDefault: boolean;
  enumProcedureActionCategoryID: string;
}

class ProcedureTableViewData extends TdoTableBaseItemModel implements ITdoTableItemModel {
  name: string;
  actions = [TdoTableActionsEnum.DELETE];
  model: Procedure;

  getActionTooltip(actionName: string): string {
    return 'Delete procedure';
  }
}

@Component({
  selector: 'app-procedure-selection-dialog',
  templateUrl: './procedure-selection-dialog.component.html',
  styleUrls: ['./procedure-selection-dialog.component.scss']
})
export class ProcedureSelectionDialogComponent implements OnInit {
  procedures: Procedure[] = [];
  selectedProcedure: Procedure = null;

  // proceduresTableDisplayedColumns = ['name', 'actions'];
  proceduresTableDisplayedColumns = ['name'];
  proceduresTableDisplayedColumnsLabels = ['Name'];
  proceduresTableModel: TdoTableModel;
  proceduresTableDataSource: MatTableDataSource<ProcedureTableViewData>;
  proceduresTableViewData: ProcedureTableViewData[];

  procedureActionCategories: EnumValidValueItemModel[];

  isLoading = true;

  constructor(private staticDataService: StaticDataService,
              private proceduresService: ProceduresService,
              private dialogService: DialogService,
              public dialog: MatDialog,
              public dialogRef: MatDialogRef<ProcedureSelectionDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private data: ProcedureSelectionDialogModel,
              private snackBar: MatSnackBar) { }

  async ngOnInit(): Promise<void> {
    if (this.staticDataService.isReady) {
      this.procedureActionCategories = this.staticDataService.getValidValueItemsByValidValueName('enumProcedureActionCategory');
    } else {
      this.staticDataService.validValuesReadyEvent.subscribe({
        complete: () => {
          this.procedureActionCategories = this.staticDataService.getValidValueItemsByValidValueName('enumProcedureActionCategory');
        }
      });
    }

    if (!this.data || (this.data && !this.data.datasource)) {
      try {
        const result = await this.proceduresService.getApplicationProcedures().toPromise();

        this.procedures = result.map<Procedure>(o => ({
          id: o.CaseProcedureId,
          enumProcedureActionCategoryID: o.enumProcedureActionCategoryID,
          code: o.Code,
          description: o.Description,
          name: o.ProcedureName,
          forms: o.cfgCaseProcedureForms.length !== 0 ?
            o.cfgCaseProcedureForms.map<Form>(o => ({
              id: o.CaseProcedureFormId,
              dFormID: (o as any).cfgDForm ? (o as any).cfgDForm.DFormId : '',
              name: (o as any).cfgDForm ? (o as any).cfgDForm.DFormName : 'No Form Name',
              filledBy: o.enumFormFilledByID,
              isOpenByDefault: o.isOpenByDefault,
              isAddByDefault: o.isAddByDefault,
              formOrder: o.formOrder
            })) : [],
          deletedForms: [],
          speciality: o.enumMedicalSpecialtyID,
          partOfAppointmentGroup: false,
          appointmentAddByDefault: false,
          partOfVisitGroup: false,
          visitAddByDefault: false
        }));

        this.refreshProcedureTableData(this.procedures);
        this.selectedProcedure = this.procedures[0];
        this.isLoading = false;
      } catch (e) {
        console.log(e);
      }
    } else {
      this.refreshProcedureTableData(this.data.datasource);
      this.selectedProcedure = this.procedures[0];
    }
  }

  refreshProcedureTableData(procedures: Procedure[]) {
    this.proceduresTableViewData = Array.from(procedures, (_) => {
      const model = new ProcedureTableViewData();
      model.name = _.name;
      model.model = _;
      model.actions = [TdoTableActionsEnum.DELETE];
      return model;
    });

    if (!this.proceduresTableModel) {
      this.proceduresTableDataSource = new MatTableDataSource<ProcedureTableViewData>(this.proceduresTableViewData);
      this.proceduresTableModel = new TdoTableModel(this.proceduresTableDisplayedColumns, this.proceduresTableDisplayedColumnsLabels, this.proceduresTableDataSource, 'name', 'asc');
      this.proceduresTableModel.searchPredicate = (data: ProcedureTableViewData, filter: string) => {
        if (data.name.toLowerCase().includes(filter)) {
          return true;
        }
      };
    } else {
      this.proceduresTableDataSource.data = this.proceduresTableViewData;
      this.proceduresTableDataSource._updateChangeSubscription();
    }
  }

  onProcedureTdoTableItemActionClick(event: TdoTableEvent): void {
    switch (event.action) {
      case TdoTableActionsEnum.DELETE:
        const model = (event.value as ProcedureTableViewData).model;
        this.dialogService.openConfirmDialog(`Are you sure you want to delete ${model.name}?`).subscribe((result) => {
          if (result === true) {
            // TODO: Need to hit service to actually delete the procedure persistence
          } else {
            //
          }
        });
        break;
    }
  }

  onProcedureTdoTableItemClick(event: TdoTableEvent): void {
    const model = (event.value as ProcedureTableViewData).model;
    this.selectedProcedure = model;
  }

  onCancelClick() {
    this.dialogRef.close(null);
  }

  onOkClick() {
    this.dialogRef.close(this.selectedProcedure);
  }
}
